<!-- @migration-task Error while migrating Svelte code: $$props is used together with named props in a way that cannot be automatically migrated. -->
<script lang="ts">
    import { ConfigurationManager } from "../../ConfigurationManager";
    import type { IGamesController } from "../../controller";
    import { Slot, SLOT_TYPES } from "../../model/Slot";
    import { getAuthInfo, type AuthInfo } from "../../utility/AccountUtility";
    import { getMessagingService, MESSAGE_TYPE, type MessagingService } from "../../utility/ContextHelper.svelte";
    import BootstrapVectorIcon from "../components/BootstrapVectorIcon.svelte";
    import InplaceEditField from "../components/InplaceEditField.svelte";
    import PromisedComponentHandler from "../components/PromisedComponentHandler.svelte";
    import ScreenshotGallery from "./ScreenshotGallery.svelte";
    import ScreenshotGalleryCard from "./ScreenshotGalleryCard.svelte";


    let {
        slot,
        includeSummaryStats = false,
        showBookingButton = false,
        showLinkToStats = false,
        showLinkToPromptEngineering = false,
        showLinkToCopySlot = false,
        class: Classname = "",
        style = "",
        horizontal = false
    }: {
        slot: Slot
        includeSummaryStats?: boolean,
        showBookingButton?: boolean,
        showLinkToStats?: boolean | Promise<boolean>,
        showLinkToPromptEngineering?: boolean | Promise<boolean>,
        showLinkToCopySlot?: boolean | Promise<boolean>,
        "class"?: string,
        style?: string,
        horizontal?: boolean
    } = $props();

    let auth: AuthInfo = getAuthInfo();

    function resolveOwnerStatus(): Promise<boolean> {
        return Promise.resolve(slot?.game).then((game) => {
            if (game) {
                return game.isOwner(auth);
            }

            return slot.isOwner(auth);
        });
    }

    let canEditSlot: Promise<boolean> = $derived(resolveOwnerStatus());

    let actualShowLinkToStats: boolean = $state(false);
    let actualShowLinkToPromptEngineering: boolean = $state(false);
    let actualShowLinkToCopySlot: boolean = $state(false);

    $effect(() => {
        Promise.all([
            resolveOwnerStatus(),
            Promise.resolve(showLinkToStats),
            Promise.resolve(showLinkToPromptEngineering),
            Promise.resolve(showLinkToCopySlot),
        ]).then(([isOwner, canShowStats, canShowPrompt, canShowCopy]) => {
            actualShowLinkToStats = canShowStats && isOwner;
            actualShowLinkToPromptEngineering = canShowPrompt && isOwner;
            actualShowLinkToCopySlot = canShowCopy && isOwner;
        });
    });

        /**
     * Set to true to display this as a horizontal card
     */

     let msgService: MessagingService = getMessagingService();

     function copyToClipboardCallback() {
        navigator.clipboard.writeText(slot.id);
        msgService.BoradcastMessage("Copied slot id to clipboard", MESSAGE_TYPE.INFO);
     }


     async function updateSlotData(val: string | boolean): Promise<boolean> {
        let slotCntrl: IGamesController = ConfigurationManager.getInstance().gamesController;
        const previousTransparency = slot.transparency;

        if(typeof val === "boolean") {
            slot.transparency = val;
        }

        try {
            let newSlot: Slot = await slotCntrl.storeSlotData(slot);
        } catch(e) {
            if(typeof val === "boolean") {
                slot.transparency = previousTransparency;
            }
            msgService.BroadCastError("Could not update slot data");
            return false;
        }
        msgService.BoradcastMessage(
            typeof val === "boolean" ? "Slot transparency updated successfully" : "Slot data updated successfully",
            MESSAGE_TYPE.SUCCESS
        );

        return true;
     }

</script>

<div class=" {Classname}"  style="{style || ""}">

    <div class="h-100 mb-0 card ">
        <div class="row mx-0 {horizontal ? "card-horizontal w-100" : ""}">

            {#if slot.screenshots[0]}
                <div class="{horizontal ? "col-4" : "col-12"} cover-img-wrapper {horizontal ? "card-img-left" : "card-img-top p-0"}">
                    <ScreenshotGallery screenshots={slot.screenshots} class="w-100"></ScreenshotGallery>
                </div>
            {:else}
                <img src="assets/demo/slot_generic.png" class="cover-img-wrapper {horizontal ? "col-4 card-img-left" : "col-12 card-img-top p-0"}" alt="No screenshot available">
            {/if}

            <div class="{horizontal ? "col-8" : "col-12"} card-body d-flex flex-column">
                <h3 class="card-title">
                    <InplaceEditField hasEditRights={slot.isOwner(auth)} bind:content={slot.name} callback={updateSlotData}></InplaceEditField>
                </h3>
        
                <div class="row w-100 mx-0">

                    <h3 class="h6 small text-info text-uppercase mb-3">About</h3>

                    <ul class="list-unstyled mb-7 pe-0">
                        <li class="py-2">
                            <BootstrapVectorIcon
                                icon="credit-card-2-front"
                                style="height: 1em; width: 1em;"
                                class="me-2"
                            ></BootstrapVectorIcon>
                            {slot.id}
                            <button type="button" class="btn btn-link p-0 bg-transparent " onclick={copyToClipboardCallback} style="float: right;">
                                <BootstrapVectorIcon
                                    icon="copy"
                                    style="height: 2em; width: 2em;"
                                ></BootstrapVectorIcon>
                            </button>
                        </li>
                        <li class="py-2">
                            <BootstrapVectorIcon
                                icon="code-square"
                                style="height: 1em; width: 1em;"
                                class="me-2"
                            ></BootstrapVectorIcon>
                            
                            {#if slot.type == SLOT_TYPES.TEXTURE}
                                Texture
                            {:else if slot.type == SLOT_TYPES.OBJECT_3D}
                                3D Object
                            {:else if slot.type == SLOT_TYPES.COMPLEX}
                                Complex ({slot.slots.length})
                            {/if}
                            
                        </li>
                        {#if slot.type == SLOT_TYPES.TEXTURE}
                            <li class="py-2">
                                <BootstrapVectorIcon
                                    icon="aspect-ratio"
                                    style="height: 1em; width: 1em;"
                                    class="me-2"
                                ></BootstrapVectorIcon>
                                {slot.dimensions[0]} : {slot.dimensions[1]}
                            </li>
                            <li class="py-2 d-flex align-items-center">
                                <BootstrapVectorIcon
                                    icon="circle-half"
                                    style="height: 1em; width: 1em;"
                                    class="me-2 flex-shrink-0"
                                ></BootstrapVectorIcon>
                                <div class="flex-fill">
                                    <InplaceEditField
                                        inputType="checkbox"
                                        trueLabel="Transparent"
                                        falseLabel="Solid"
                                        checkboxLabel="Enable transparency"
                                        bind:checked={slot.transparency}
                                        callback={updateSlotData}
                                        hasEditRights={slot.isOwner(auth)}
                                    ></InplaceEditField>
                                </div>
                            </li>
                        {/if}
                        <li class="py-2">
                            <BootstrapVectorIcon
                                icon="controller"
                                style="height: 1em; width: 1em;"
                                class="me-2"
                            ></BootstrapVectorIcon>
                            <a href="#/game/{slot.gameId}">
                                <PromisedComponentHandler retrievalPromise={slot.game}>
                                    {#snippet children(game)}
                                        {game.name}                                        
                                    {/snippet}
                                </PromisedComponentHandler>
                            </a>
                        </li>
                        <li class="py-2" style="max-height: 7.2em; overflow-y: auto;">
                            <InplaceEditField bind:content={slot.description} callback={updateSlotData} multiline={true} hasEditRights={slot.isOwner(auth)}></InplaceEditField>
                        </li>
                    </ul>
                </div>
            </div>

        </div>
        <div class="mt-auto mb-4 mx-4 text-end">
            {#if showBookingButton}
                <a class="btn btn-primary align-self-baseline" href="#/book/slot/{slot.id}">Book</a>        
            {/if}
            {#if actualShowLinkToCopySlot}
                <a class="btn btn-primary align-self-baseline" href="#/game/{slot.gameId}/slot/{slot.id}">Copy</a>        
            {/if}
            {#if actualShowLinkToStats}
                <a class="btn btn-primary align-self-baseline" href="#/slot/{slot.id}">Stats</a>
            {/if}
            {#if actualShowLinkToPromptEngineering}
                <a class="btn btn-primary align-self-baseline" href="#/promptengineering/slot/{slot.id}">Edit Prompt</a>
            {/if}
        </div>
    </div>

</div>
